export { SemanticEvent, fromSignal, fromCustomEvent, isOncePerPage };

/**
 * Mapa señal-del-SDK -> slug de `system_conversion_event` (el catálogo de
 * api-marketing). El SDK habla SIEMPRE en slugs: los nombres de evento de
 * cada red los pone el manifest, aquí no se conoce ninguno.
 *
 * Añadir un evento de navegador nuevo cuesta: una fila en el diccionario del
 * backend, una entrada en este mapa y su default en cada red. Ni UI ni
 * adaptadores.
 */

const SemanticEvent = {
  PageView: "page_view",
  ContentView: "content_view",
  VideoWatched50: "video_watched_50",
  AppointmentSlotSelected: "appointment_slot_selected",
  UserOptinCreated: "user_optin_created",
  OpportunityCreated: "opportunity_created",
} as const;

/**
 * Señales que YA emite el SDK y que se traducen a evento semántico al pasar
 * por `do('webanalytics')`:
 *
 * - `onWebinarVideoStart`: arranque del VSL/webinar (lo re-emite el relay del
 *   collector; el plugin solo lo manda a GTM/DOM).
 * - `onWebinarVideoViewedDecile6`: el plugin numera el decil N y lo emite al
 *   ENTRAR en el tramo (N-1)x10 %, así que el decil 6 es "≥ 50 % visto".
 *
 * `page_view`, `user_optin_created` y `opportunity_created` NO están aquí: se
 * disparan desde sus puntos de enganche en EventCollector (init, 2xx del lead
 * y saveScheduledEvent), no desde un nombre de evento de analítica.
 */
const SIGNAL_TO_SLUG: Record<string, string> = {
  onWebinarVideoStart: SemanticEvent.ContentView,
  onWebinarVideoViewedDecile6: SemanticEvent.VideoWatched50,
};

/**
 * Señales que llegan por `emitCustomEvent` (GTM + DOM) y no por
 * `do('webanalytics')`:
 *
 * - `onCalendlyDatetimeSelected`: el visitante elige fecha y hora en el
 *   Calendly de la landing (plugin de Calendly). Es la intención previa a la
 *   reserva; en GTM lo medía la conversión «High Intent Lead» con la primera
 *   de la página. Se acepta también la grafía `DateTime` que reconocía GTM.
 */
const CUSTOM_EVENT_TO_SLUG: Record<string, string> = {
  onCalendlyDatetimeSelected: SemanticEvent.AppointmentSlotSelected,
  onCalendlyDateTimeSelected: SemanticEvent.AppointmentSlotSelected,
};

// Eventos de los que la red espera UNO por carga de página. Los duales
// (optin, cita) no se deduplican aquí: ya vienen deduplicados de su origen
// (ventana de 20 min por email, flag de Vault por uuid de la cita).
const ONCE_PER_PAGE: Record<string, boolean> = {
  [SemanticEvent.PageView]: true,
  [SemanticEvent.ContentView]: true,
  [SemanticEvent.VideoWatched50]: true,
  [SemanticEvent.AppointmentSlotSelected]: true,
};

function fromSignal(eventName: unknown): string | null {
  if (typeof eventName !== "string") {
    return null;
  }
  return SIGNAL_TO_SLUG[eventName] ?? null;
}

function fromCustomEvent(eventName: unknown): string | null {
  if (typeof eventName !== "string") {
    return null;
  }
  return CUSTOM_EVENT_TO_SLUG[eventName] ?? null;
}

function isOncePerPage(slug: string): boolean {
  return ONCE_PER_PAGE[slug] === true;
}
